<script setup>
import { useI18n } from 'vue-i18n';
import { isEmpty } from 'lodash';
import {ElForm, ElFormItem} from 'element-plus';
import 'element-plus/es/components/form/style/css';
import 'element-plus/es/components/form-item/style/css'

const { t } = useI18n();
</script>
<script>
import find from 'lodash/find';
import {mapState} from 'pinia';
import {useEnvStore} from '@dutypay/store-modules'
import screenSize from './../../mixins/screenSize'

export default {
  name: "DpFormDeletionConfirmation",
  inheritAttrs: true,
  mixins: [screenSize],
  props: {
    action: {required: true},
    notificationSuccess: {type: Object},
    notificationAsyncError: {type: Object},
    disableNotification: [Boolean, Array],
    deleteButtonLabel: {type: String},
    keepButtonLabel: {type: String},
    deleteButtonParameter: {type: Object},
    keepButtonParameter: {type: Object},
    triggerAction: {type: Boolean}
  },
  data() {
    return {
      isFormActive: false,
      isLoading: false
    };
  },
  computed: {
    ...mapState(useEnvStore, ['debug']),
    notification() {
      return {
        success: (this.notificationSuccess !== undefined) ? this.notificationSuccess : {
          title: this.$t('notification.success.title'),
          message: this.$t('notification.success.message')
        },
        asyncError: (this.notificationAsyncError !== undefined) ? this.notificationAsyncError : {
          title: this.$t('notification.asyncError.title'),
          message: this.$t('notification.asyncError.message')
        }
      }
    },
  },
  watch: {
    triggerAction: function (val) {
      if (val) this.submitForm();
    }
  },
  methods: {
    cancelForm() {
      this.deactivateForm();
      this.$emit('cancel-form');
    },
    submitForm() {
      if (this.isFormActive) {
        this.executeDeletion();
      } else {
        this.activateForm();
      }
    },
    activateForm() {
      this.isFormActive = true;
    },
    deactivateForm() {
      this.isFormActive = false;
    },
    async executeDeletion() {
      try {
        this.isLoading = true;
        let res = await this.action();
        this.deactivateForm();
        this.isLoading = false;
        if (res.status >= 400 && res.status <= 600) {
          this.notifyAsyncError(this.notification.asyncError);
          this.$emit('asyncError');
          return;
        }
        this.notifySuccess(this.notification.success);
        this.$emit('success');
      } catch (e) {
        this.notifyAsyncError(this.notification.asyncError);
        this.$emit('asyncError');
        if (this.debug)
          return e;
      }
    },
    containsKey(key) {
      return find(this.disableNotification, (iteratee) => {return (iteratee === key)}) !== undefined;
    },
    notifyAsyncError(message) {
      if (this.disableNotification === true || this.containsKey('asyncError'))
        return;
      this.$notify.error(message);
    },
    notifySuccess(message) {
      if (this.disableNotification === true || this.containsKey('success'))
        return;
      this.$notify.success(message);
    }
  }
}
</script>

<template>
  <el-form
      v-bind="$attrs"
      v-loading="isLoading"
      @submit.prevent>

    <el-form-item v-if="isFormActive">
      <dp-button purpose="secondaryAction" v-bind="keepButtonParameter" @click="cancelForm">
        {{(isEmpty(keepButtonLabel)) ? t('keep.buttonLabel') : keepButtonLabe}}
      </dp-button>
    </el-form-item>
    <el-form-item>
      <dp-button purpose="destructiveAction" v-bind="deleteButtonParameter" @click="submitForm">
        {{(isEmpty(deleteButtonLabel)) ? t('delete.buttonLabel') : deleteButtonLabel}}
      </dp-button>
    </el-form-item>

  </el-form>
</template>

<style scoped lang="scss">
</style>

<i18n>
{
  "ar-AE": {
    "delete": {
      "buttonLabel": "حذف"
    },
    "keep": {
      "buttonLabel": "لتحفظ"
    }
  },
  "ar-EG": {
    "delete": {
      "buttonLabel": "حذف"
    },
    "keep": {
      "buttonLabel": "لتحفظ"
    }
  },
  "ar-SA": {
    "delete": {
      "buttonLabel": "حذف"
    },
    "keep": {
      "buttonLabel": "لتحفظ"
    }
  },
  "de-DE": {
    "delete": {
      "buttonLabel": "Löschen"
    },
    "keep": {
      "buttonLabel": "Behalten"
    }
  },
  "de-AT": {
    "delete": {
      "buttonLabel": "Löschen"
    },
    "keep": {
      "buttonLabel": "Behalten"
    }
  },
  "de-CH": {
    "delete": {
      "buttonLabel": "Löschen"
    },
    "keep": {
      "buttonLabel": "Behalten"
    }
  },
  "en-US": {
    "delete": {
      "buttonLabel": "Delete"
    },
    "keep": {
      "buttonLabel": "Keep"
    }
  },
  "en-GB": {
    "delete": {
      "buttonLabel": "Delete"
    },
    "keep": {
      "buttonLabel": "Keep"
    }
  },
  "it-IT": {
    "delete": {
      "buttonLabel": "Elimina"
    },
    "keep": {
      "buttonLabel": "Mantenere"
    }
  },
  "fr-FR": {
    "delete": {
      "buttonLabel": "Effacer"
    },
    "keep": {
      "buttonLabel": "Garder"
    }
  },
  "es-ES": {
    "delete": {
      "buttonLabel": "Eliminar"
    },
    "keep": {
      "buttonLabel": "Mantener"
    }
  },
  "pl-PL": {
    "delete": {
      "buttonLabel": "Usunąć"
    },
    "keep": {
      "buttonLabel": "Zachować"
    }
  },
  "nl-NL": {
    "delete": {
      "buttonLabel": "Verwijderen"
    },
    "keep": {
      "buttonLabel": "Houden"
    }
  },
  "bg-BG": {
    "delete": {
      "buttonLabel": "Изтрий"
    },
    "keep": {
      "buttonLabel": "Да запазя"
    }
  },
  "bs-BA": {
    "delete": {
      "buttonLabel": "Izbriši"
    },
    "keep": {
      "buttonLabel": "Zadržati"
    }
  },
  "da-DK": {
    "delete": {
      "buttonLabel": "Slet"
    },
    "keep": {
      "buttonLabel": "At beholde"
    }
  },
  "et-EE": {
    "delete": {
      "buttonLabel": "Kustuta"
    },
    "keep": {
      "buttonLabel": "Pidama"
    }
  },
  "fi-FI": {
    "delete": {
      "buttonLabel": "Poistaa"
    },
    "keep": {
      "buttonLabel": "Pitää"
    }
  },
  "el-GR": {
    "delete": {
      "buttonLabel": "Διαγράφω"
    },
    "keep": {
      "buttonLabel": "Κρατώ"
    }
  },
  "he-IL": {
    "delete": {
      "buttonLabel": "לִמְחוֹק"
    },
    "keep": {
      "buttonLabel": "לשמור"
    }
  },
  "ga-IE": {
    "delete": {
      "buttonLabel": "Scrios"
    },
    "keep": {
      "buttonLabel": "A choimeád"
    }
  },
  "ja-JP": {
    "delete": {
      "buttonLabel": "削除"
    },
    "keep": {
      "buttonLabel": "保つ"
    }
  },
  "hr-HR": {
    "delete": {
      "buttonLabel": "Izbrisati"
    },
    "keep": {
      "buttonLabel": "Zadržati"
    }
  },
  "lv-LV": {
    "delete": {
      "buttonLabel": "Dzēst"
    },
    "keep": {
      "buttonLabel": "Paturēt"
    }
  },
  "lt-LT": {
    "delete": {
      "buttonLabel": "Ištrinti"
    },
    "keep": {
      "buttonLabel": "Pasilikti"
    }
  },
  "pt-PT": {
    "delete": {
      "buttonLabel": "Excluir"
    },
    "keep": {
      "buttonLabel": "Manter"
    }
  },
  "ro-RO": {
    "delete": {
      "buttonLabel": "Șterge"
    },
    "keep": {
      "buttonLabel": "Păstra"
    }
  },
  "ru-RU": {
    "delete": {
      "buttonLabel": "Удаление"
    },
    "keep": {
      "buttonLabel": "Хранить"
    }
  },
  "sv-SE": {
    "delete": {
      "buttonLabel": "Radera"
    },
    "keep": {
      "buttonLabel": "Behålla"
    }
  },
  "sk-SK": {
    "delete": {
      "buttonLabel": "Odstrániť"
    },
    "keep": {
      "buttonLabel": "Udržať"
    }
  },
  "cs-CZ": {
    "delete": {
      "buttonLabel": "Vymazat"
    },
    "keep": {
      "buttonLabel": "Zachovat"
    }
  },
  "tr-TR": {
    "delete": {
      "buttonLabel": "Sil"
    },
    "keep": {
      "buttonLabel": "Saklamak"
    }
  },
  "hu-HU": {
    "delete": {
      "buttonLabel": "Töröl"
    },
    "keep": {
      "buttonLabel": "Tartani"
    }
  },
  "fr-BE": {
    "delete": {
      "buttonLabel": "Effacer"
    },
    "keep": {
      "buttonLabel": "Garder"
    }
  },
  "fr-LU": {
    "delete": {
      "buttonLabel": "Effacer"
    },
    "keep": {
      "buttonLabel": "Garder"
    }
  },
  "mt-MT": {
    "delete": {
      "buttonLabel": "Ħassar"
    },
    "keep": {
      "buttonLabel": ""
    }
  },
  "sl-SI": {
    "delete": {
      "buttonLabel": "Izbriši"
    },
    "keep": {
      "buttonLabel": "Obdržati"
    }
  },
  "el-CY": {
    "delete": {
      "buttonLabel": "Διαγράφω"
    },
    "keep": {
      "buttonLabel": "Κρατώ"
    }
  },
  "tr-CY": {
    "delete": {
      "buttonLabel": "Sil"
    },
    "keep": {
      "buttonLabel": "Saklamak"
    }
  },
  "uk-UA": {
    "delete": {
      "buttonLabel": "Löschen"
    },
    "keep": {
      "buttonLabel": "Тримати"
    }
  },
  "zh-CN": {
    "delete": {
      "buttonLabel": "删除"
    },
    "keep": {
      "buttonLabel": "保持"
    }
  }
}
</i18n>
